import React from "react";

// Inline SVG Berber motifs — used as decorative dividers and accents across the site.
// All motifs render at any width via preserveAspectRatio + viewBox. The stroke colour
// defaults to the brand gold (#D4A373) and is overridable via the `tone` prop.

const TONES = {
  gold: "#D4A373",
  umber: "#A07042",
  ink: "#2C2621",
  cream: "#FDFBF7",
};

// ── 1. Diamond chain (kilim/zellige inspired) — for major section breaks
export const BerberDiamondDivider = ({ tone = "gold", className = "", strokeWidth = 0.8, opacity = 1 }) => {
  const color = TONES[tone] || tone;
  return (
    <div
      className={`relative flex items-center justify-center select-none pointer-events-none ${className}`}
      role="presentation"
      aria-hidden="true"
      data-testid="berber-divider-diamond"
    >
      <span className="h-px flex-1 bg-current opacity-30" style={{ color }} />
      <svg
        viewBox="0 0 200 22"
        className="mx-4 h-5 md:h-[22px]"
        style={{ color, opacity }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round">
          {[20, 60, 100, 140, 180].map((cx) => (
            <g key={`d-${cx}`}>
              <path d={`M${cx} 4 L${cx + 8} 11 L${cx} 18 L${cx - 8} 11 Z`} />
              <path d={`M${cx} 8 L${cx + 4} 11 L${cx} 14 L${cx - 4} 11 Z`} fill="currentColor" />
            </g>
          ))}
          <line x1="28" y1="11" x2="52" y2="11" />
          <line x1="68" y1="11" x2="92" y2="11" />
          <line x1="108" y1="11" x2="132" y2="11" />
          <line x1="148" y1="11" x2="172" y2="11" />
        </g>
      </svg>
      <span className="h-px flex-1 bg-current opacity-30" style={{ color }} />
    </div>
  );
};

// ── 2. Zigzag (carpet edge) — for lighter transitions
export const BerberZigzagDivider = ({ tone = "gold", className = "", strokeWidth = 1, opacity = 1 }) => {
  const color = TONES[tone] || tone;
  return (
    <div
      className={`relative flex items-center justify-center select-none pointer-events-none ${className}`}
      role="presentation"
      aria-hidden="true"
      data-testid="berber-divider-zigzag"
    >
      <svg
        viewBox="0 0 400 14"
        preserveAspectRatio="none"
        className="w-full h-3.5"
        style={{ color, opacity }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <polyline
          points="0,7 16,2 32,12 48,2 64,12 80,2 96,12 112,2 128,12 144,2 160,12 176,2 192,12 208,2 224,12 240,2 256,12 272,2 288,12 304,2 320,12 336,2 352,12 368,2 384,12 400,7"
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

// ── 3. Star burst (Amazigh medallion) — for hero/feature accents
export const BerberStarAccent = ({ tone = "gold", className = "", size = 32 }) => {
  const color = TONES[tone] || tone;
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      style={{ color }}
      aria-hidden="true"
      data-testid="berber-star-accent"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* 8-point star */}
        <path d="M16 2 L19 13 L30 16 L19 19 L16 30 L13 19 L2 16 L13 13 Z" />
        <circle cx="16" cy="16" r="3" fill="currentColor" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="16" y1="26" x2="16" y2="30" />
        <line x1="2" y1="16" x2="6" y2="16" />
        <line x1="26" y1="16" x2="30" y2="16" />
      </g>
    </svg>
  );
};

// ── 4. Triangle band (Berber tribal motif) — for borders and small accents
export const BerberTriangleBand = ({ tone = "umber", className = "", height = 10 }) => {
  const color = TONES[tone] || tone;
  return (
    <svg
      viewBox="0 0 100 10"
      preserveAspectRatio="none"
      className={className}
      style={{ width: "100%", height: `${height}px`, color }}
      aria-hidden="true"
      data-testid="berber-triangle-band"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fill="currentColor">
        {Array.from({ length: 20 }).map((_, i) => (
          <polygon
            key={`tri-${i}`}
            points={`${i * 5},10 ${i * 5 + 2.5},2 ${i * 5 + 5},10`}
            opacity={i % 2 === 0 ? 0.85 : 0.5}
          />
        ))}
      </g>
    </svg>
  );
};

// ── 5. Section corner mark (used at start/end of editorial blocks)
export const BerberCorner = ({ tone = "gold", className = "", size = 24 }) => {
  const color = TONES[tone] || tone;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      style={{ color }}
      aria-hidden="true"
      data-testid="berber-corner"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <path d="M2 12 L22 12" />
        <path d="M12 2 L12 22" />
        <circle cx="12" cy="12" r="3" />
        <path d="M5 5 L9 9" />
        <path d="M19 5 L15 9" />
        <path d="M5 19 L9 15" />
        <path d="M19 19 L15 15" />
      </g>
    </svg>
  );
};

export default BerberDiamondDivider;
